import { useEffect, useState } from 'react'
import Button from '../../../../components/form-control/Button';
import { findRenewedPoliciesByPolicyNumber, markNonMotorPolicyAsRenewed, markPolicyAsRenewed } from '../../../../api/requests';
import { useNavigate } from 'react-router-dom';
import useRoute from '../../../../hooks/routesHook';
import Loader from '../../../../components/Loader';
import { AlertCircle, Info } from 'lucide-react';
import { getFormattedDate, showToast } from '../../../../utils/miscFunctions';
import { PolicySummary } from '../../../../models/policy/PolicySummary';
import { useQuery } from '@tanstack/react-query';
import usePolicy from '../../../../hooks/policyHook';

interface IMarkPolicyAsRenewed {
  customerName: string
  policyNumber: string;
  policyType: string;
  vehicleMake?: string;
  vehicleModel?: string;
  onclose?: () => void;
  onProceed?: () => void;
}

const MarkPolicyAsRenewed = ({ customerName, policyNumber, policyType, vehicleMake, vehicleModel, onclose, onProceed, }: IMarkPolicyAsRenewed) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMarkingRenewed, setIsMarkingRenewed] = useState<boolean>(false);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicySummary | null>(null);
  const [error, setError] = useState<string>('');
  const { getDashboardBase } = useRoute();
  const navigate = useNavigate();
  const policyId = policyNumber;
  const isMotorPolicy = policyType == 'VEHICLE';
  const { PolicyStatusBadge } = usePolicy()
  const selectedPolicyNumber = selectedPolicy?.policyNumber

  const handleFetchPolicy = async () => {
    setError('');
    setIsLoading(true);

    setTimeout(() => {
      try {
        if (isMotorPolicy) {
          navigate(`${getDashboardBase()}/policies/auto-policy/add-issued-motor-policy${policyNumber ? `?policyNumber=${policyNumber}` : ''}`);
        } else {
          navigate(`${getDashboardBase()}/policies/dynamic-create-policy?type=non-motor${policyNumber ? `&policyNumber=${policyNumber}` : ''}`);
        }
      } catch (error: any) {
        const errorMessage = error.description ?? 'Failed to navigate to renewal form. Please try again.';
        setError(errorMessage);
        showToast.error(errorMessage,);
      } finally {
        setIsLoading(false);
      }
    }, 2000);
  };

  const handleMarkAsRenewed = async () => {
    if (!selectedPolicyNumber?.trim()) {
      setError('Please enter a renewed policy number');
      return;
    }

    setIsMarkingRenewed(true);
    setError('');

    try {
      // Motor policies live in `policies`, non-motor in `non_motor_policy`.
      // The two mark-renewed endpoints only look in their own table — calling
      // the wrong one returns 404 "Policy not found", which surfaced as
      // "No policy found" for bulk-uploaded non-motor renewals.
      const markFn = isMotorPolicy ? markPolicyAsRenewed : markNonMotorPolicyAsRenewed;
      const response: any = await markFn(policyId, selectedPolicyNumber?.trim());
      if (response) {
        showToast.success(response.message ?? 'Policy successfully marked as renewed',);
        onclose?.();
        onProceed?.();

      }
    } catch (error: any) {
      const errorMessage = error?.description ?? 'Failed to mark policy as renewed. Please try again.';
      setError(errorMessage);
      showToast.error(errorMessage,);

      // If marking as renewed fails, fall back to fetching policy for renewal
      if (error?.status === 404) {
        setTimeout(() => {
          handleFetchPolicy();
        }, 4000);
      } else return

    } finally {
      setIsMarkingRenewed(false);
    }
  };

  const handleSubmit = () => {
    if (selectedPolicyNumber?.trim()) {
      handleMarkAsRenewed();
    } else {
      handleFetchPolicy();
    }
  };

  const handleSelectPolicy = (policy: PolicySummary) => {
    setSelectedPolicy((prevSelected) => {
      const isCurrentlySelected = prevSelected?.id === policy.id || prevSelected?.policyNumber === policy.policyNumber;
      return isCurrentlySelected ? null : policy;
    })
  };

  // policyNumber MUST be part of the queryKey — the modal is reused across
  // Renewals list rows, and without keying on policyNumber React Query serves
  // the cached results from whichever row opened the modal first ("popup
  // shows the previously selected policy" report). Same reason we reset
  // selectedPolicy + error when policyNumber changes below.
  const { data: searchResults } = useQuery({
    queryKey: ['findRenewedPoliciesByPolicyNumber', policyNumber],
    queryFn: async () => {
      const response = await findRenewedPoliciesByPolicyNumber(policyNumber);
      return response?.map((policy: any) => new PolicySummary(policy));
    },
    enabled: !!policyNumber,
  });

  useEffect(() => {
    setSelectedPolicy(null);
    setError('');
  }, [policyNumber]);


  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <p className="text-sm text-gray-600 dark:text-darkBg-subText">
          {isMotorPolicy ? 'Motor' : 'Non-Motor'} Policy: {policyNumber}
        </p>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-8 space-y-3">
          <Loader size="sm" brollyLoaderFixed message={'Fetching old policy details...Review and save for renewed policy.'} />
        </div>
      )}

      {/* Form Content */}
      {!isLoading && (
        <div className="space-y-6">

          {/* Infotip and Search Dropdown */}
          <div className="space-y-4">
            {/* Infotip */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm space-y-2">
                  <p className="text-amber-800 dark:text-amber-200 font-medium">
                    Select the renewed policy below to proceed
                  </p>
                  <p className="text-amber-700 dark:text-amber-300">
                    Or Add the renewed policy if policy has not yet been added to the dashboard
                  </p>
                </div>
              </div>
            </div>

            {/* Renewed Policies */}
            {/* Renewed Policies Modern Card List */}
            <div className="bg-white/80 dark:bg-darkBg backdrop-blur rounded-2xl p-4 shadow-md border border-gray-100 dark:border-gray-800">

              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  Renewed Policies Found
                </p>
                <p className="text-xs text-gray-500 dark:text-darkBg-subText">
                  Click a policy below to link it as the renewal
                </p>
              </div>

              <ul className="max-h-72 overflow-y-auto space-y-3 pr-1">
                {searchResults?.length ? (
                  searchResults.map((policy: any) => {
                    const isSelected =
                      selectedPolicy?.id === policy.id ||
                      selectedPolicy?.policyNumber === policy.policyNumber;

                    return (
                      <li key={policy.id || policy.policyNumber}>
                        <button
                          type="button"
                          onClick={() => handleSelectPolicy(policy)}
                          className={`w-full text-left p-4 rounded-xl transition-all duration-200 border
                              ${isSelected ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md "
                              : "border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/60 hover:shadow-sm hover:border-gray-300 dark:hover:border-gray-600 hover:scale-[1.01]"
                            }`}>

                          <div className="flex justify-between items-start">
                            <div className="space-y-1">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                {policy.customerName || `${policy.firstName} ${policy.lastName}`}
                              </p>

                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {policy.policyNumber}
                              </p>

                              {(policy.registrationNumber || policy.vehicleRegNumber) && (
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                  {policy.make && `Vehicle: ${policy.make} ${policy.model || null}`}
                                  {policy.registrationNumber && ` • Reg: ${policy.registrationNumber}`}
                                </p>
                              )}

                              {(policy.inceptionDate) && (
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                  {policy.inceptionDate && `Duration: ${getFormattedDate({ date: policy.inceptionDate })}`}
                                  {policy.expiresOn && ` - ${getFormattedDate({ date: policy.expiresOn })}`}
                                </p>
                              )}
                            </div>

                            <div className="flex flex-col items-end gap-2">
                              <PolicyStatusBadge status={policy.active} />

                              {isSelected && (
                                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                  Selected
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })
                ) : (
                  <li className="py-8 text-center text-sm text-gray-500 dark:text-darkBg-subText">
                    No renewed policies found for this policy.
                  </li>
                )}
              </ul>
            </div>

            {!!error &&
              <p className="text-xs text-gray-500 dark:text-darkBg-subText">
                Required if the renewed policy has already been added to the Brolly dashboard
              </p>
            }
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">
                  {error}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              label="Cancel"
              onClick={onclose}
              bgColor='secondary'
              fullWidth
              disabled={isLoading || isMarkingRenewed}
            />
            <Button
              type="submit"
              label={selectedPolicyNumber ? "Mark as renewed" : 'Add renewed policy'}
              bgColor='primary'
              fullWidth
              onClick={handleSubmit}
              loading={isLoading || isMarkingRenewed}
              disabled={isLoading || isMarkingRenewed || !!error}
            />
          </div>

          {/* Additional Info */}
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-darkBg-subText">
              {selectedPolicyNumber?.trim() && "This will mark the existing policy as renewed with the selected policy."}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default MarkPolicyAsRenewed


